// MarqueeCard.jsx
// Card-type marquee messages — richer than scrolling text, used primarily in
// booth-context mode for credentials/menu/feature beats. Held in the marquee
// slot for the message's durationSec.
//
// Layouts:
//   credentials — "AS SEEN ON" + press-logo strip (uses /public/press/*.png)
//   menu        — pricing block ("TODAY AT THE BOOTH" + items)
//   feature     — title + body + optional image (general highlight)
//
// Accepts a `compact` flag for slim header rendering (cards are mainly designed
// for billboard, but the same component renders in either container so the
// editor preview matches what shows on stream).

import FadeImg from "./FadeImg";

const PRESS_LOGOS = [
  { name: "Today",         file: "/press/Today_Show_Logo.png" },
  { name: "Good Morning America", file: "/press/Good_Morning_America_Logo.png" },
  { name: "Hell's Kitchen", file: "/press/Hells_Kitchen_Logo.png" },
  { name: "Disney Parks",  file: "/press/Disney_Parks_Logo.png" },
  { name: "Nickelodeon", file: "/press/Nickelodeon_logo.svg" },
];

function CredentialsCard({ msg, fontSize, compact }) {
  const title = (msg.card?.title || "AS SEEN ON").trim();
  return (
    <div style={{
      display: "flex", flexDirection: compact ? "row" : "column",
      alignItems: "center", justifyContent: "center", gap: compact ? "0.8rem" : "0.45rem",
      width: "100%",
    }}>
      <div style={{
        color: "var(--jop-gold)",
        fontFamily: "var(--jop-display)",
        fontSize: compact ? fontSize : `calc(${fontSize} * 0.9)`,
        fontWeight: 900,
        letterSpacing: "0.14em",
        textShadow: "0 2px 8px rgba(var(--jop-gold-rgb), 0.35)",
        whiteSpace: "nowrap",
      }}>
        {title}
      </div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: compact ? "0.7rem" : "1.4rem",
        flexWrap: "nowrap", overflow: "hidden",
      }}>
        {PRESS_LOGOS.map((p) => (
          <FadeImg
            key={p.name}
            src={p.file}
            alt={p.name}
            style={{
              height: compact ? "1.15em" : "2.4em",
              width: "auto",
              maxWidth: "9em",
              objectFit: "contain",
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.4))",
            }}
          />
        ))}
      </div>
    </div>
  );
}

function MenuCard({ msg, fontSize, compact }) {
  const title = (msg.card?.title || "TODAY AT THE BOOTH").trim();
  const items = Array.isArray(msg.card?.items) && msg.card.items.length
    ? msg.card.items
    : [
        { label: "Flash Pancake Art", price: "$10" },
        { label: "Custom Pancake Art", price: "$30" },
        { label: "Gluten-Free Poffertjes", price: "—" },
      ];
  return (
    <div style={{
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      gap: compact ? "0.2rem" : "0.45rem",
      width: "100%",
    }}>
      <div style={{
        color: "var(--jop-gold)",
        fontFamily: "var(--jop-display)",
        fontSize: compact ? fontSize : `calc(${fontSize} * 0.85)`,
        fontWeight: 900,
        letterSpacing: "0.12em",
        textShadow: "0 2px 8px rgba(var(--jop-gold-rgb), 0.3)",
      }}>
        {title}
      </div>
      <div style={{
        display: "flex",
        flexDirection: compact ? "row" : "row",
        flexWrap: compact ? "nowrap" : "wrap",
        justifyContent: "center",
        gap: compact ? "1.4rem" : "1.8rem",
        color: "white",
        fontFamily: "var(--jop-display)",
        fontWeight: 800,
        fontSize: compact ? `calc(${fontSize} * 0.9)` : fontSize,
      }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: "flex", alignItems: "baseline", gap: "0.5rem" }}>
            <span>{it.label}</span>
            <span style={{ color: "var(--jop-cream)" }}>{it.price}</span>
            {it.note ? (
              <span style={{
                color: "rgba(255,255,255,0.55)",
                fontSize: `calc(${fontSize} * 0.7)`,
                fontWeight: 600,
              }}>
                {it.note}
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function FeatureCard({ msg, fontSize, compact }) {
  const title = (msg.card?.title || msg.text || "").trim();
  const body = (msg.card?.body || "").trim();
  const img = msg.card?.imageUrl;
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      gap: compact ? "0.8rem" : "1.4rem",
      width: "100%",
    }}>
      {img ? (
        <FadeImg
          src={img}
          alt=""
          style={{
            height: compact ? "1.8em" : "2.6em",
            width: "auto",
            objectFit: "contain",
            borderRadius: "8px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.35)",
          }}
        />
      ) : null}
      <div style={{
        display: "flex", flexDirection: "column",
        alignItems: img ? "flex-start" : "center",
        textAlign: img ? "left" : "center",
        gap: "0.15rem",
      }}>
        {title ? (
          <div style={{
            color: "var(--jop-gold)",
            fontFamily: "var(--jop-display)",
            fontSize,
            fontWeight: 900,
            letterSpacing: "0.04em",
            lineHeight: 1.05,
            textShadow: "0 2px 8px rgba(var(--jop-gold-rgb), 0.3)",
          }}>
            {title}
          </div>
        ) : null}
        {body ? (
          <div style={{
            color: "white",
            fontFamily: "var(--jop-body)",
            fontSize: `calc(${fontSize} * 0.78)`,
            fontWeight: 700,
            lineHeight: 1.2,
          }}>
            {body}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const LAYOUTS = {
  credentials: CredentialsCard,
  menu: MenuCard,
  feature: FeatureCard,
};

export const CARD_LAYOUT_OPTIONS = [
  { id: "credentials", label: "Credentials (As Seen On)" },
  { id: "menu", label: "Menu / Pricing" },
  { id: "feature", label: "Feature highlight" },
];

export default function MarqueeCard({ msg, fontSize = "2.1rem", compact = false }) {
  const layout = msg?.card?.layout || "feature";
  const Component = LAYOUTS[layout] || FeatureCard;
  return <Component msg={msg} fontSize={fontSize} compact={compact} />;
}
